import { Canvas } from '@react-three/fiber'
import { WeatherWidget } from 'features/kestrelViewer'
import { ChevronLeft, ChevronRight, HelpCircle, X } from 'lucide-react'
import React, { FC, useCallback, useEffect, useRef, useState } from 'react'
import styled, { keyframes } from 'styled-components'
import { Vector3 } from 'three'
import { GeckoKestrelModel } from 'types'

import { AnnotationList } from './components/AnnotationList'
import { ControlsHelp } from './components/ControlsHelp'
import { MapStyle, MapStyleHotbar } from './components/MapStyleHotbar'
import {
  Sidebar,
  SidebarContent,
  SidebarSection,
  SidebarSectionTitle,
} from './components/Sidebar'
import {
  CanvasContainer,
  Container,
  HotbarRow,
  SidebarToggle,
} from './components/styles'
import { TilesScene } from './components/TilesScene'
import { useHasKestrel3DTiles } from './hooks/useHasKestrel3DTiles'
import { useLayerManager } from './hooks/useLayerManager'
import {
  Kestrel3DTilesProvider,
  useKestrel3DTiles,
} from './Kestrel3DTilesProvider'
import { ReadOnlyLayerHotbar } from './ReadOnlyLayerHotbar'
import { AxisConfig } from './utils/annotations'

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(0.5rem);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const HelpButton = styled.button`
  position: absolute;
  bottom: 1rem;
  right: 1rem;
  width: 2.5rem;
  height: 2.5rem;
  background: rgba(0, 0, 0, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 100;
  backdrop-filter: blur(4px);

  &:hover {
    background: rgba(0, 0, 0, 0.7);
    transform: scale(1.1);
  }

  svg {
    width: 1.25rem;
    height: 1.25rem;
  }
`

const HelpOverlay = styled.div`
  position: absolute;
  bottom: 1rem;
  right: 1rem;
  background: rgba(0, 0, 0, 0.85);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 0.5rem;
  padding: 0.75rem;
  padding-top: 1.5rem;
  color: white;
  z-index: 100;
  backdrop-filter: blur(8px);
  animation: ${fadeIn} 0.2s ease-out;
  min-width: 200px;
`

const CloseButton = styled.button`
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: white;
  }

  svg {
    width: 1rem;
    height: 1rem;
  }
`

interface ReadOnly3DTilesViewerProps {
  kestrelModelId: string
  kestrelModel: GeckoKestrelModel
}

const ReadOnly3DTilesViewerInner: FC<ReadOnly3DTilesViewerProps> = ({
  kestrelModelId,
  kestrelModel,
}) => {
  const { availableLayers } = useHasKestrel3DTiles(kestrelModelId)
  const tiles3DContext = useKestrel3DTiles()

  // Viewer settings
  const showAxes = false
  const [showAnnotations, setShowAnnotations] = useState(true)
  const [showLabels, setShowLabels] = useState(true)
  const [showArea, setShowArea] = useState(false)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [flyToTarget, setFlyToTarget] = useState<Vector3 | null>(null)
  const [mapStyle, setMapStyle] = useState<MapStyle>('street')
  const [mapLoading, setMapLoading] = useState(false)
  const [helpVisible, setHelpVisible] = useState(false)

  // Layer management
  const {
    layers,
    activeLayer,
    loadingLayer,
    handleLayerChange,
    handleLayerLoaded,
  } = useLayerManager({
    availableLayers,
    setTilesFolder: tiles3DContext?.setTilesFolder,
  })

  const firstRendererRef = useRef<unknown>(null)
  const canvasContainerRef = useRef<HTMLDivElement>(null)

  // Track if the viewer is active (clicked into)
  const [isActive, setIsActive] = useState(false)

  const handleCanvasClick = useCallback(() => {
    setIsActive(true)
  }, [])

  // Deactivate when clicking outside the canvas container
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        canvasContainerRef.current &&
        !canvasContainerRef.current.contains(e.target as Node)
      ) {
        setIsActive(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Annotation axis mapping
  const axisConfig: AxisConfig = {
    xFrom: 'x',
    yFrom: 'z',
    zFrom: 'y',
    xSign: -1,
    ySign: 1,
    zSign: -1,
  }

  return (
    <Container>
      <Sidebar $collapsed={sidebarCollapsed}>
        <SidebarContent>
          <SidebarSection>
            <HotbarRow>
              <ReadOnlyLayerHotbar
                availableLayers={availableLayers}
                activeLayer={activeLayer}
                loadingLayer={loadingLayer}
                onLayerChange={handleLayerChange}
              />
              <MapStyleHotbar
                mapStyle={mapStyle}
                onStyleChange={setMapStyle}
                loadingStyle={mapLoading ? mapStyle : null}
              />
            </HotbarRow>
          </SidebarSection>

          <SidebarSection>
            <AnnotationList
              annotations={kestrelModel.data.annotations ?? []}
              axisConfig={axisConfig}
              onFlyTo={setFlyToTarget}
              showAnnotations={showAnnotations}
              onToggleVisibility={() => setShowAnnotations(!showAnnotations)}
              showLabels={showLabels}
              onToggleLabels={setShowLabels}
              showArea={showArea}
              onToggleArea={setShowArea}
            />
          </SidebarSection>

          {kestrelModel.data.scanDate && kestrelModel.data.origin && (
            <SidebarSection>
              <SidebarSectionTitle>Weather</SidebarSectionTitle>
              <WeatherWidget
                flightDate={kestrelModel.data.scanDate}
                latitude={kestrelModel.data.origin.lat}
                longitude={kestrelModel.data.origin.long}
              />
            </SidebarSection>
          )}
        </SidebarContent>
      </Sidebar>

      <CanvasContainer ref={canvasContainerRef} onClick={handleCanvasClick}>
        <SidebarToggle onClick={() => setSidebarCollapsed(!sidebarCollapsed)}>
          {sidebarCollapsed ? (
            <ChevronRight size={16} />
          ) : (
            <ChevronLeft size={16} />
          )}
        </SidebarToggle>

        {/* Help button/overlay */}
        {helpVisible ? (
          <HelpOverlay>
            <CloseButton
              onClick={() => setHelpVisible(false)}
              style={{ position: 'absolute', top: '0.5rem', right: '0.5rem' }}>
              <X />
            </CloseButton>
            <ControlsHelp />
          </HelpOverlay>
        ) : (
          <HelpButton
            onClick={() => setHelpVisible(true)}
            title='Show controls'>
            <HelpCircle />
          </HelpButton>
        )}

        <Canvas
          style={{
            position: 'relative',
            height: '100%',
            width: '100%',
            backgroundColor: '#1a1a2e',
          }}
          onError={(error) =>
            console.error('[ReadOnly3DTiles] Canvas error:', error)
          }>
          <TilesScene
            kestrelModelId={kestrelModelId}
            layers={layers}
            showAxes={showAxes}
            showAnnotations={showAnnotations}
            showLabels={showLabels}
            showArea={showArea}
            mapStyle={mapStyle}
            origin={kestrelModel.data.origin}
            annotations={kestrelModel.data.annotations ?? []}
            axisConfig={axisConfig}
            flyToTarget={flyToTarget}
            firstRendererRef={firstRendererRef}
            onLayerLoaded={handleLayerLoaded}
            onLayerRendererReady={(renderer, index) => {
              if (index === 0 && !firstRendererRef.current) {
                firstRendererRef.current = renderer
              }
            }}
            onFlyToComplete={() => setFlyToTarget(null)}
            setControls={(controls) => tiles3DContext?.setControls(controls)}
            isActive={isActive}
            onMapLoadingChange={setMapLoading}
          />
        </Canvas>
      </CanvasContainer>
    </Container>
  )
}

export const ReadOnly3DTilesViewer: FC<ReadOnly3DTilesViewerProps> = ({
  kestrelModelId,
  kestrelModel,
}) => (
  <Kestrel3DTilesProvider>
    <ReadOnly3DTilesViewerInner
      kestrelModelId={kestrelModelId}
      kestrelModel={kestrelModel}
    />
  </Kestrel3DTilesProvider>
)
